import React, { useRef, useEffect } from "react";
import * as d3 from "d3";

function forceBoundary({
  x0 = 0,
  x1,
  y0 = 0,
  y1,
  radius = 12,
  restitution = 0.8
} = {}) {
  let nodes;

  function force() {
    for (const node of nodes) {
      if (node.x < x0 + radius) {
        node.x = x0 + radius;
        node.vx = -node.vx * restitution;
      } else if (node.x > x1 - radius) {
        node.x = x1 - radius;
        node.vx = -node.vx * restitution;
      }

      if (node.y < y0 + radius) {
        node.y = y0 + radius;
        node.vy = -node.vy * restitution;
      } else if (node.y > y1 - radius) {
        node.y = y1 - radius;
        node.vy = -node.vy * restitution;
      }
    }
  }

  force.initialize = function(initNodes) {
    nodes = initNodes;
  };

  return force;
}

const ForceGraph = ({
  width = 600,
  height = 400,
  nodeCount = 20,
}) => {
  const svgRef = useRef(null);

  useEffect(() => {
    const pastelColors = ["#ffb3b3", "#ffffb3", "#b3ffb3", "#b3b3ff"];

    // Create nodes
    const nodes = Array.from({ length: nodeCount }, (_, i) => ({
      id: i,
      color: pastelColors[i % 4],
    }));

    // No initial links
    const links = [];

    // Select the SVG and make it responsive
    const svgEl = d3.select(svgRef.current)
      .attr("viewBox", `0 0 ${width} ${height}`)   // keep 600×400 internally
      .style("width", "100%")                      // responsive to container
      .style("height", "auto");

    // Setup the simulation
    const simulation = d3.forceSimulation(nodes)
      .force("charge", d3.forceManyBody().strength(-25))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(12))
      .alphaDecay(0.02)
      .force("boundary", forceBoundary({
        x0: 0,
        x1: width,
        y0: 0,
        y1: height,
        radius: 12,
        restitution: 0.1
      }))
      .on("tick", ticked);

    // Periodically add/remove links
    const intervalId = setInterval(() => {
      if (links.length >= 10) return;

      const [source, target] = d3.shuffle(nodes).slice(0, 2);
      const existingLink = links.find(
        (l) =>
          (l.source === source && l.target === target) ||
          (l.source === target && l.target === source)
      );

      const getDegree = (node) =>
        links.filter((l) => l.source === node || l.target === node).length;

      if (!existingLink) {
        // Add link if both have <2 connections
        if (getDegree(source) < 2 && getDegree(target) < 2) {
          links.push({ source, target });
        }
      } else {
        // Remove the link
        links.splice(links.indexOf(existingLink), 1);
      }

      simulation.force("link", d3.forceLink(links).distance(50).strength(0.5));
      simulation.alpha(0.3).restart();
    }, 1000);

    // Create groups for links and nodes
    const linkGroup = svgEl.append("g")
      .attr("stroke", "#fff")
      .attr("stroke-opacity", 0.6);

    const nodeGroup = svgEl.append("g")
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5);

    function ticked() {
      // Links
      const linkSelection = linkGroup
        .selectAll("line")
        .data(links, (d) => `${d.source.id}-${d.target.id}`);

      linkSelection
        .enter()
        .append("line")
        .merge(linkSelection)
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);

      linkSelection.exit().remove();

      // Nodes
      const nodeSelection = nodeGroup
        .selectAll("circle")
        .data(nodes, (d) => d.id);

      nodeSelection
        .enter()
        .append("circle")
        .attr("r", 10)
        .attr("fill", (d) => d.color)
        .merge(nodeSelection)
        .attr("cx", (d) => d.x)
        .attr("cy", (d) => d.y);

      nodeSelection.exit().remove();
    }

    // Cleanup
    return () => {
      simulation.stop();
      clearInterval(intervalId);
    };
  }, [width, height, nodeCount]);

  return <svg ref={svgRef} />;
};

export default ForceGraph;
